// Imports
import { useState, Dispatch, SetStateAction, useRef, useCallback, useEffect } from 'react';
import merge from 'lodash/merge';
import { z } from 'zod';


/** A custom Hook that behaves just like `useState()`, except that it stores its data using the browser history API so that it can rehydrate itself automatically. You’ll need to provide a unique storage key as the second parameter, using kabob case (`example-name`). */
export default function useRecoveredState<S>(defaultValue: S, storageKey: string): [S, Dispatch<SetStateAction<S>>] {
	// Throw error storage key has wrong casing
	if (storageKey.match(/[^a-z0-9-]/) !== null) {
		throw new Error('Storage keys must use kebab casing');
	}
	
	
	// Attempt to recover state on mount
	const hasMounted = useRef(false);
	
	interface DataNotRecovered {
		value: null;
		found: false;
	}
	
	interface DataRecovered {
		value: S;
		found: true;
	}
	
	let recoveredData: DataNotRecovered | DataRecovered = {
		value: null,
		found: false,
	};
	
	if (hasMounted.current === false) {
		hasMounted.current = true;
		
		try {
			const stateSchema = z.object({
				state: z.object({
					[storageKey]: z.string(),
				}),
			});
			
			const { state } = stateSchema.parse(window.history.state);
			
			recoveredData = {
				value: JSON.parse(state[storageKey]) as S,
				found: true,
			};
		} catch (ignore) {
			// Do nothing
		}
	}
	
	
	// Keep track of component's mounted state
	const isMounted = useRef(true);
	
	useEffect(() => {
		isMounted.current = true;
		
		return () => {
			isMounted.current = false;
		};
	}, []);
	
	
	// Use state
	const [data, setData] = useState(
		recoveredData.found &&
			!(window as typeof window & { STORYBOOK?: true }).STORYBOOK &&
			(!process.env.JEST_WORKER_ID || (window as typeof window & { ALLOW_RECOVERY?: boolean }).ALLOW_RECOVERY)
			? recoveredData.value
			: defaultValue
	);
	
	
	// Memoize a setter function
	const setter: Dispatch<SetStateAction<S>> = useCallback(
		(newData) => {
			// Stringify the data for later use
			const stringifiedNewData = JSON.stringify(newData);
			
			
			// Save to browser history
			if (isMounted.current) {
				window.history.replaceState(
					merge(window.history.state || {}, {
						state: {
							[storageKey]: stringifiedNewData,
						},
					}),
					''
				);
			}
			
			
			// Save to local state data
			setData(newData);
		},
		[storageKey]
	);
	
	
	// Return
	return [data, setter];
}
